import React, { useEffect } from 'react';
import {
  QAlert,
  QEmptyState,
  QLink,
  QSpinner,
  QStack,
  QSubtitle,
  QText,
  useCurrentUser,
  useToastProvider,
} from '@qualio/ui-components';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { getCompanyFeatures } from '../../../api/medtech-api';
import { BetaFeaturesControl } from './BetaFeaturesControl/BetaFeaturesControl';
import * as flagProvider from '../../../utils/flagProvider';

export const BetaFeatures = () => {
  const currentUser = useCurrentUser();
  const { t } = useTranslation();
  const { isFlagEnabled } = flagProvider.useFlagProvider();
  const { showToast } = useToastProvider();

  const { isLoading, data, isError } = useQuery(
    ['company-features', currentUser.companyId],
    () => {
      return getCompanyFeatures(currentUser.companyId);
    },
    {
      // Toggling a feature writes its new value straight into this cache, so staleTime
      // keeps a refetch from replacing that with a server value that may not have caught
      // up with the change yet.
      staleTime: 60_000,
    },
  );

  useEffect(() => {
    if (isError) {
      showToast({
        'data-cy': 'beta-features-fetch-error-toast',
        id: 'fetch-features',
        replace: true,
        description: t('messages.error.fetchFeatures'),
        status: 'error',
      });
    }
  }, [showToast, isError, t]);

  if (isLoading || data === undefined) {
    return <QSpinner size="sm" />;
  }

  const subHeading = t('betaFeatures.description');

  const helpCenterLink = (
    <QLink href="https://docs.qualio.com/en/articles/8681740-current-open-betas" isExternal>
      {t('betaFeatures.helpCenter')}
    </QLink>
  );

  if (!data?.features || data.features.length === 0) {
    return (
      <QEmptyState title={t('emptyStates.noBetaFeatures.title')} subtitle={t('emptyStates.noBetaFeatures.subtitle')} />
    );
  }

  return (
    <QStack spacing="10">
      <QSubtitle>
        {subHeading} {helpCenterLink} {t('betaFeatures.packageNotice')}
        {isFlagEnabled('termsOfService') && (
          <QStack style={{ marginTop: '20px' }}>
            <QAlert
              status="info"
              data-cy="tos-alert-beta"
              isInline
              description={
                <QText data-cy="tos-text-beta">
                  {t('betaFeatures.termsOfService.prefix')}{' '}
                  <QLink href="https://www.qualio.com/docs/terms" isExternal>
                    {t('betaFeatures.termsOfService.terms')}
                  </QLink>{' '}
                  {t('betaFeatures.termsOfService.middle')}{' '}
                  <QLink href="https://www.qualio.com/docs/terms" isExternal>
                    {t('betaFeatures.termsOfService.suffix')}
                  </QLink>
                </QText>
              }
            />
          </QStack>
        )}
      </QSubtitle>
      {data.features.map((feature) => (
        <BetaFeaturesControl
          key={feature.key}
          featureKey={feature.key}
          description={feature.description}
          name={feature.name}
          active={feature.active}
        />
      ))}
    </QStack>
  );
};
